import { isProduction } from '@/common/environment';
import { VariableInfo } from '@/lib/App';

export enum EXTERNAL_PORTAL {
  CRADLE_STG = 'http://cradle.stg-myteksi.com/',
  CRADLE_PRD = 'http://cradle.grab.com/',
  GAMMA_STG = 'https://gamma-econs.stg-myteksi.com/fares',
  GAMMA_PRD = 'https://gamma-econs.grab.com/fares',
  LINGO_STG = 'https://lingo-web.stg-myteksi.com/projects',
  LINGO_PRD = 'https://lingo-web.grab.com/projects',
  PRICING_CONTROL_CENTRE_STG = 'https://pricing-control-centre.stg-myteksi.com/',
  PRICING_CONTROL_CENTRE_PRD = 'https://pricing-control-centre.grab.com/pricing',
}

export enum UNSUPPORTED_ROLLOUT_SERVICE {
  PRICING_TRANSPORT = 'pax-pricing-transport',
  PRICING_FOOD = 'pax-pricing-food',
  CRADLE_WHEELS = 'grab-wheels',
  WHEELS = 'wheels',
  PRICING = 'pax-pricing',
  LINGO_SDK = 'lingosdk-*',
}

export const UNSUPPORTED_ROLLOUT_SERVICES = [
  UNSUPPORTED_ROLLOUT_SERVICE.PRICING_TRANSPORT,
  UNSUPPORTED_ROLLOUT_SERVICE.PRICING_FOOD,
  UNSUPPORTED_ROLLOUT_SERVICE.CRADLE_WHEELS,
  UNSUPPORTED_ROLLOUT_SERVICE.WHEELS,
  UNSUPPORTED_ROLLOUT_SERVICE.PRICING,
  UNSUPPORTED_ROLLOUT_SERVICE.LINGO_SDK,
];

enum UNSUPPORTED_ROLLOUT_SERVICE_CATEGORY {
  PRICING = 'pricing',
  CRADLE = 'wheels',
  LINGO = 'lingo',
}

export const UNSUPPORTED_ROLLOUT_SERVICE_CATEGORY_MAP = {
  [UNSUPPORTED_ROLLOUT_SERVICE.PRICING_TRANSPORT]:
    UNSUPPORTED_ROLLOUT_SERVICE_CATEGORY.PRICING,
  [UNSUPPORTED_ROLLOUT_SERVICE.PRICING_FOOD]:
    UNSUPPORTED_ROLLOUT_SERVICE_CATEGORY.PRICING,
  [UNSUPPORTED_ROLLOUT_SERVICE.PRICING]:
    UNSUPPORTED_ROLLOUT_SERVICE_CATEGORY.PRICING,
  [UNSUPPORTED_ROLLOUT_SERVICE.CRADLE_WHEELS]:
    UNSUPPORTED_ROLLOUT_SERVICE_CATEGORY.CRADLE,
  [UNSUPPORTED_ROLLOUT_SERVICE.WHEELS]:
    UNSUPPORTED_ROLLOUT_SERVICE_CATEGORY.CRADLE,
  [UNSUPPORTED_ROLLOUT_SERVICE.LINGO_SDK]:
    UNSUPPORTED_ROLLOUT_SERVICE_CATEGORY.LINGO,
};

function validateServiceWildcardSupport(backend: string): boolean {
  if (!backend) return false;

  if (backend.match(/\*/g).length > 1) return false;

  if (!backend.startsWith('*') && !backend.endsWith('*')) return false;

  return true;
}

export function generateLingoUrl(variableInfo: VariableInfo): string {
  let url = isProduction
    ? EXTERNAL_PORTAL.LINGO_PRD
    : EXTERNAL_PORTAL.LINGO_STG;

  if (!validateServiceWildcardSupport(UNSUPPORTED_ROLLOUT_SERVICE.LINGO_SDK))
    console.error('invalid service for enum UNSUPPORTED_ROLLOUT_SERVICE');

  const serviceWithoutWildcard =
    UNSUPPORTED_ROLLOUT_SERVICE.LINGO_SDK.toString().replace(/\*/g, '');
  const lingoBackendRegex = new RegExp(`${serviceWithoutWildcard}`, 'g');
  let backend = variableInfo.backend.replace(lingoBackendRegex, '');
  const lingoVariablePrefixRegex = new RegExp(`lingo__${backend}__`, 'g');
  let variableName = variableInfo.name.replace(lingoVariablePrefixRegex, '');

  return `${url}/${backend}?search=${variableName}`;
}

export function getUnsupportedService(backend: string): string {
  const nonWildcardUnsupportServices = UNSUPPORTED_ROLLOUT_SERVICES.filter(
    (service) => !service.includes('*'),
  );
  const wildcardUnsupportServices = UNSUPPORTED_ROLLOUT_SERVICES.filter(
    (service) => service.includes('*'),
  );

  let unsupportedService: string;

  nonWildcardUnsupportServices.forEach((service) => {
    if (service === backend) {
      unsupportedService = service;
      return false;
    }
  });

  if (unsupportedService) return unsupportedService;

  wildcardUnsupportServices.forEach((service) => {
    if (
      !validateServiceWildcardSupport(UNSUPPORTED_ROLLOUT_SERVICE.LINGO_SDK)
    ) {
      console.error('invalid service for enum UNSUPPORTED_ROLLOUT_SERVICE');

      return false;
    }

    const serviceWithoutWildcard = service.toString().replace(/\*/g, '');

    if (service.startsWith('*') && backend.endsWith(serviceWithoutWildcard)) {
      unsupportedService = service;
      return false;
    }

    if (service.endsWith('*') && backend.startsWith(serviceWithoutWildcard)) {
      unsupportedService = service;
      return false;
    }
  });

  return unsupportedService;
}

export function getExternalUrl(variableInfo: VariableInfo): string {
  const unsupportedService = getUnsupportedService(variableInfo?.backend);

  if (!unsupportedService) return '';

  switch (unsupportedService) {
    case UNSUPPORTED_ROLLOUT_SERVICE.PRICING_TRANSPORT:
    case UNSUPPORTED_ROLLOUT_SERVICE.PRICING_FOOD: {
      return isProduction
        ? EXTERNAL_PORTAL.PRICING_CONTROL_CENTRE_PRD
        : EXTERNAL_PORTAL.PRICING_CONTROL_CENTRE_STG;
    }
    case UNSUPPORTED_ROLLOUT_SERVICE.WHEELS:
    case UNSUPPORTED_ROLLOUT_SERVICE.CRADLE_WHEELS: {
      return isProduction
        ? EXTERNAL_PORTAL.CRADLE_PRD
        : EXTERNAL_PORTAL.CRADLE_STG;
    }
    case UNSUPPORTED_ROLLOUT_SERVICE.LINGO_SDK: {
      return generateLingoUrl(variableInfo);
    }
    default: {
      return isProduction
        ? EXTERNAL_PORTAL.GAMMA_PRD
        : EXTERNAL_PORTAL.GAMMA_STG;
    }
  }
}
